import React from 'react'
import { useI18n } from '../state/I18nContext'

const WhyCaladinhos: React.FC = () => {
  const { locale } = useI18n()
  const isEn = locale === 'en'

  const content = isEn
    ? {
      title: '🥖 Caladinhos: a sweet, a password, a story of resistance.',
      intro:
        'Caladinhos from Santa Maria da Feira are traditional biscuits whose origins are wrapped in legend, ingenuity, and popular memory. More than a sweet, they became a symbol of complicit silence and resistance during the Estado Novo.',
      originTitle: '📜 The Origin',
      originP1:
        'The best-known version of the Caladinhos story dates back to the 1930s, at the height of the Salazar dictatorship.',
      originP2:
        'At that time, opponents of the regime met clandestinely in discreet places such as bakeries and cafés, to exchange ideas and information without raising suspicion.',
      legendTitle: '🕯️ The Legend of Resistance',
      contextTitle: 'Context',
      contextP:
        'During the Estado Novo, any suspicious gathering could lead to trouble with the authorities. Bakeries, open through the night and operating as continuous workplaces, became unlikely — and therefore useful — places for discreet meetings.',
      episodeTitle: 'The Episode',
      episodeP:
        'In 1934, at the bakery of Augusto “Padeiro” (Augusto Valente d\'Almeida), on Rua Direita, a group was gathered when they were surprised by police agents. To avoid reprisals, Augusto told his helpers to keep silent and murmured:',
      episodeQuote: '“Chiu! Caladinhos!”',
      ruseTitle: 'The Ruse',
      ruseP1: 'Asked about the reason for the silence, the baker improvised:',
      ruseQuote: '— “Because we are making Caladinhos.”',
      ruseP2: 'The police believed it, left, and the name ended up being associated with the biscuit.',
      codeTitle: '🔔 The Code of Silence',
      codeP1:
        'After that episode, “Caladinho!” began to be used as a warning signal in public places.',
      codeP2:
        'In cafés and pastry shops in town, whenever someone suspicious entered, it was enough to hear:',
      codeQuote: '“Well then, one Caladinho for this table!”',
      codeP3: 'and everyone knew it was time to keep quiet.',
      imageAlt: 'Traditional Caladinhos biscuits',
    }
    : {
      title: '🥖 Os Caladinhos: um doce, uma senha, uma história de resistência.',
      intro:
        'Os Caladinhos de Santa Maria da Feira são biscoitos tradicionais cuja origem está envolta em lenda, engenho e memória popular. Mais do que um doce, tornaram-se símbolo de silêncio cúmplice e resistência durante o Estado Novo.',
      originTitle: '📜 A Origem',
      originP1:
        'A versão mais conhecida da história dos Caladinhos remonta aos anos 30 do século XX, em plena ditadura salazarista.',
      originP2:
        'Nessa época, opositores ao regime reuniam-se clandestinamente em locais discretos, como padarias e cafés, para trocar ideias e informações sem levantar suspeitas.',
      legendTitle: '🕯️ A Lenda da Resistência',
      contextTitle: 'Contexto',
      contextP:
        'Durante o Estado Novo, qualquer reunião suspeita podia levantar problemas com as autoridades. As padarias, por funcionarem de madrugada e serem espaços de trabalho contínuo, tornaram-se locais improváveis — e por isso úteis — para encontros discretos.',
      episodeTitle: 'O Episódio',
      episodeP:
        'Em 1934, na padaria de Augusto “Padeiro” (Augusto Valente d\'Almeida), na Rua Direita, um grupo estaria reunido quando foi surpreendido por agentes da polícia. Para evitar represálias, Augusto mandou os ajudantes ficarem em silêncio e murmurou:',
      episodeQuote: '“Chiu! Caladinhos!”',
      ruseTitle: 'O Engano',
      ruseP1: 'Questionado sobre o motivo do silêncio, o padeiro improvisou:',
      ruseQuote: '— “Porque estamos a fazer Caladinhos.”',
      ruseP2: 'A polícia acreditou, foi-se embora, e o nome acabou por ficar associado ao biscoito.',
      codeTitle: '🔔 O Código do Silêncio',
      codeP1:
        'Depois desse episódio, “Caladinho!” passou a ser usado como sinal de alerta em locais públicos.',
      codeP2:
        'Em cafés e pastelarias da vila, quando alguém suspeito entrava, bastava ouvir-se:',
      codeQuote: '“Ora, saia um Caladinho para esta mesa!”',
      codeP3: 'e todos sabiam que era hora de fazer silêncio.',
      imageAlt: 'Caladinhos tradicionais',
    }

  return (
    <section className="max-w-6xl mx-auto px-4 py-12">
      <div className="grid gap-10 md:grid-cols-2 md:items-start">
        <div className="space-y-6 text-neutral-800">
          <div className="space-y-3">
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
              {content.title}
            </h1>
            <p className="text-lg text-neutral-700">
              {content.intro}
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-2xl font-bold">{content.originTitle}</h2>
            <p>
              {content.originP1}
            </p>
            <p>
              {content.originP2}
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-2xl font-bold">{content.legendTitle}</h2>
            <h3 className="text-lg font-semibold">{content.contextTitle}</h3>
            <p>
              {content.contextP}
            </p>
            <h3 className="text-lg font-semibold">{content.episodeTitle}</h3>
            <p>
              {content.episodeP}
              <span className="block mt-2 font-semibold">{content.episodeQuote}</span>
            </p>
            <h3 className="text-lg font-semibold">{content.ruseTitle}</h3>
            <p>
              {content.ruseP1}
              <span className="block mt-2 font-semibold">{content.ruseQuote}</span>
              {content.ruseP2}
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-2xl font-bold">{content.codeTitle}</h2>
            <p>
              {content.codeP1}
            </p>
            <p>
              {content.codeP2}
              <span className="block mt-2 font-semibold">{content.codeQuote}</span>
              {content.codeP3}
            </p>
          </div>
        </div>

        <div className="md:sticky md:top-24">
          <div className="rounded-2xl overflow-hidden shadow-lg border border-neutral-200">
            <img
              src="/img/decor/cal_hist_01.png"
              alt={content.imageAlt}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  )
}

export default WhyCaladinhos
